import { UserRole } from '@constants/userRoles';
import { MY_JOBS_PAGE_SIZE } from '@constants/various';
import { ICertificatesData } from '@th-types/certificates.type';
import Picture from '@th-types/picture.type';
import { IWorkerData, Skill } from '@th-types/worker.type';
import {
  Comment,
  Education,
  IWorkerEndorsementsData,
  NonPromoWorkExperience,
  PromoWorkExperience,
} from '@th-types/workerProfile.type';
import { MyJobsTabsEnum, ViewsTypesEnum } from '@worker/types/myJobs.type';
import { WorkerProfileAllowedOptions } from '@worker/types/worker-profile-types.type';
import { create } from 'zustand';

import {
  AdvancedFiltersFormValues,
  DEFAULT_ADVANCED_FILTERS,
  DEFAULT_RATE_COMPANY_MODAL_STATE,
  DEFAULT_REQUEST_RATING_MODAL_STATE,
  DEFAULT_WORKER_PROFILE_STATE,
  PaginationState,
  SearchFilters,
  UserData,
  WorkerStore,
} from './types';

const initialSearchFilters: SearchFilters = {
  companyName: '',
  companyId: null,
  address: null,
  miles: null,
};

const initialAdvancedFilters = DEFAULT_ADVANCED_FILTERS;

const initialPagination: PaginationState = {
  currentPage: 1,
  itemsPerPage: MY_JOBS_PAGE_SIZE,
};

export const getEmptyAdvancedFilters = (): AdvancedFiltersFormValues => {
  return {
    startDate: null,
    endDate: null,
    onGoing: false,
    jobLocation: {
      country: '',
      includeNational: true,
    },
    jobTitles: [],
    minHourlyEnabled: false,
    minHourlyRate: undefined,
    minDailyEnabled: false,
    minDailyRate: undefined,
  };
};

export const useWorkerStore = create<WorkerStore>((set, get) => ({
  // MyJobs UI State
  showMobileFilters: false,
  showAdvancedFilters: false,
  showConfigureModal: false,
  showGetInNetworkModal: false,
  currentView: ViewsTypesEnum.MY_JOBS,
  currentTab: MyJobsTabsEnum.NEW,

  // MyJobs Filter State
  searchFilters: initialSearchFilters,
  advancedFilters: initialAdvancedFilters,
  pagination: initialPagination,

  // User State
  user: null,
  isLoading: false,

  // Worker Profile State
  workerProfile: DEFAULT_WORKER_PROFILE_STATE,

  // Inbox UI State
  rateCompanyModal: DEFAULT_RATE_COMPANY_MODAL_STATE,
  requestRatingModal: DEFAULT_REQUEST_RATING_MODAL_STATE,

  // MyJobs Actions
  setShowAdvancedFilters: (show: boolean) => set({ showAdvancedFilters: show }),
  setShowMobileFilters: (show: boolean) => set({ showMobileFilters: show }),
  setShowConfigureModal: (show: boolean) => set({ showConfigureModal: show }),
  setShowGetInNetworkModal: (show: boolean) =>
    set({ showGetInNetworkModal: show }),
  setCurrentView: (view: ViewsTypesEnum) => set({ currentView: view }),
  setCurrentTab: (tab: MyJobsTabsEnum) => set({ currentTab: tab }),

  setSearchFilters: (filters: SearchFilters) => {
    set({ searchFilters: filters });
  },
  setAdvancedFilters: (filters: AdvancedFiltersFormValues) => {
    set({ advancedFilters: filters });
  },
  setPagination: (pagination: PaginationState) => set({ pagination }),

  // User actions
  setUser: (user: UserData) => {
    set({ user, isLoading: false });
  },
  clearUser: () => {
    set({ user: null, isLoading: false });
  },
  hasRole: (role: UserRole): boolean => {
    const { user } = get();
    if (!user) return false;

    const userRole = UserRole[role as unknown as keyof typeof UserRole];
    return user.roles?.includes(userRole) || false;
  },
  isInNetwork: (): boolean => {
    const { workerProfile } = get();
    return workerProfile.workerProfile?.isPremium ?? false;
  },

  // Worker Profile actions
  setWorkerProfile: (profile: IWorkerData) => {
    set((state) => ({
      workerProfile: { ...state.workerProfile, workerProfile: profile },
    }));
  },
  setWorkerProfileAllowedOptions: (options: WorkerProfileAllowedOptions) => {
    set((state) => ({
      workerProfile: {
        ...state.workerProfile,
        workerProfileAllowedOptions: options,
      },
    }));
  },
  setNonPromoWorkExperience: (experience: NonPromoWorkExperience[]) => {
    set((state) => ({
      workerProfile: {
        ...state.workerProfile,
        nonPromoWorkExperience: experience,
      },
    }));
  },
  setPromoWorkExperience: (experience: PromoWorkExperience[]) => {
    set((state) => ({
      workerProfile: {
        ...state.workerProfile,
        promoWorkExperience: experience,
      },
    }));
  },
  setWorkerEducation: (education: Education[]) => {
    set((state) => ({
      workerProfile: { ...state.workerProfile, workerEducation: education },
    }));
  },
  setWorkerEndorsements: (endorsements: IWorkerEndorsementsData) => {
    set((state) => ({
      workerProfile: {
        ...state.workerProfile,
        workerEndorsements: endorsements,
      },
    }));
  },
  setWorkerMediaFiles: (mediaFiles: Picture[]) => {
    set((state) => ({
      workerProfile: { ...state.workerProfile, workerMediaFiles: mediaFiles },
    }));
  },
  setWorkerReviews: (reviews: Comment[]) => {
    set((state) => ({
      workerProfile: { ...state.workerProfile, workerReviews: reviews },
    }));
  },
  setWorkerSkills: (skills: Skill[]) => {
    set((state) => ({
      workerProfile: { ...state.workerProfile, workerSkills: skills },
    }));
  },
  setWorkerCertificates: (certificates: ICertificatesData) => {
    set((state) => ({
      workerProfile: {
        ...state.workerProfile,
        workerCertificates: certificates,
      },
    }));
  },
  setWorkerJobPreferences: (preferences) => {
    set((state) => ({
      workerProfile: {
        ...state.workerProfile,
        jobPreferences: preferences,
      },
    }));
  },
  setSurveysEnabled: (enabled: boolean) => {
    set((state) => ({
      workerProfile: { ...state.workerProfile, surveysEnabled: enabled },
    }));
  },
  setWorkerProfileLoading: (loading: boolean) => {
    set((state) => ({
      workerProfile: {
        ...state.workerProfile,
        isLoadingWorkerProfile: loading,
      },
    }));
  },
  setWorkerOptionsLoading: (loading: boolean) => {
    set((state) => ({
      workerProfile: {
        ...state.workerProfile,
        isLoadingWorkerOptions: loading,
      },
    }));
  },
  clearWorkerProfile: () => {
    set({ workerProfile: DEFAULT_WORKER_PROFILE_STATE });
  },

  // Helper actions
  resetSearchFilters: () => {
    set({ searchFilters: initialSearchFilters });
  },
  resetAdvancedFilters: () => {
    set({ advancedFilters: initialAdvancedFilters });
  },
  resetPagination: () => set({ pagination: initialPagination }),

  // Combined actions
  resetAllFilters: () => {
    set({
      searchFilters: initialSearchFilters,
      advancedFilters: initialAdvancedFilters,
      pagination: initialPagination,
    });
  },

  // Navigation actions with URL sync
  navigateToView: (view: ViewsTypesEnum, tab: MyJobsTabsEnum) => {
    set({ currentView: view, currentTab: tab });
  },

  // Inbox actions
  openRateCompanyModal: (jobAssignmentId: number) => {
    set({ rateCompanyModal: { isOpen: true, jobAssignmentId } });
  },
  closeRateCompanyModal: () => {
    set({ rateCompanyModal: DEFAULT_RATE_COMPANY_MODAL_STATE });
  },
  openRequestRatingModal: ({ jobAssignmentId, isValidatedCompany }) => {
    set({
      requestRatingModal: { isOpen: true, jobAssignmentId, isValidatedCompany },
    });
  },
  closeRequestRatingModal: () => {
    set({ requestRatingModal: DEFAULT_REQUEST_RATING_MODAL_STATE });
  },
}));

// Inbox selector hooks
export const useRateCompanyModal = () =>
  useWorkerStore((state) => state.rateCompanyModal);

export const useOpenRateCompanyModal = () =>
  useWorkerStore((state) => state.openRateCompanyModal);

export const useCloseRateCompanyModal = () =>
  useWorkerStore((state) => state.closeRateCompanyModal);

export const useRequestRatingModal = () =>
  useWorkerStore((state) => state.requestRatingModal);

export const useOpenRequestRatingModal = () =>
  useWorkerStore((state) => state.openRequestRatingModal);

export const useCloseRequestRatingModal = () =>
  useWorkerStore((state) => state.closeRequestRatingModal);
